import { useState, Fragment } from 'react';
import { CheckIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/cms/ui/dialog';
import { Input } from '@/components/cms/ui/input';
import LureoButton from '@/common/LureoButton';
import { Tile } from '@/components/ui/tile';
import { cn } from '@/utils/utils';
import { SCREEN_3_OPTIONS, type DiscoverySource } from './user-info-onboarding-discovery';

export type RoleCategory =
  | 'xr_pro'
  | 'education'
  | 'marketer_agency'
  | 'spatially_curious'
  | 'other';

export type BusinessUse =
  | 'client_brand'
  | 'own_org'
  | 'personal'
  | 'not_sure';

export interface UserInfoOnboardingData {
  roleCategory: RoleCategory;
  roleOtherText?: string;
  businessUse: BusinessUse;
  discoverySource: DiscoverySource;
  discoveryOtherText?: string;
}

interface UserInfoOnboardingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: (data: UserInfoOnboardingData) => void | Promise<void>;
}

const SCREEN_1_OPTIONS: { value: RoleCategory; label: string }[] = [
  { value: 'xr_pro', label: 'I create XR or spatial experiences' },
  { value: 'education', label: 'I create experiences for teaching or learning' },
  { value: 'marketer_agency', label: 'I create campaigns, activations, or brand experiences' },
  { value: 'spatially_curious', label: "I'm curious about spatial storytelling" },
  { value: 'other', label: 'Other' }
];

const SCREEN_2_OPTIONS: { value: BusinessUse; label: string }[] = [
  { value: 'client_brand', label: 'Yes - for a client, brand, or company' },
  { value: 'own_org', label: 'Yes - for my organisation or my own business' },
  { value: 'personal', label: 'No - personal, learning, or experimentation' },
  { value: 'not_sure', label: 'Not sure yet' }
];

const STEP_LABELS = ['About you', 'Business use', 'Discovery'];

function StepMarker({ number, label, state }: { number: number; label: string; state: 'done' | 'current' | 'upcoming' }) {
  const circleClass =
    state === 'done'
      ? 'bg-lureo-emphasis border-lureo-brand-ink text-lureo-on-emphasis'
      : state === 'current'
        ? 'bg-lureo-surface border-lureo-brand-ink text-lureo-brand-ink'
        : 'bg-lureo-surface border-lureo-line-strong text-lureo-ink-subtle';
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-lureo-border-width-medium font-lureo-font-ui text-lureo-12 font-semibold leading-none transition-colors duration-lureo-duration-slow',
          circleClass
        )}
      >
        {state === 'done' ? <CheckIcon className="h-3.5 w-3.5" /> : number}
      </span>
      <span
        className={cn(
          'font-lureo-font-ui text-lureo-12 leading-[1.3]',
          state === 'upcoming' ? 'text-lureo-ink-subtle' : 'font-medium text-lureo-ink'
        )}
      >
        {label}
      </span>
    </div>
  );
}

export function UserInfoOnboardingDialog({
  open,
  onOpenChange,
  onComplete
}: UserInfoOnboardingDialogProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [roleCategory, setRoleCategory] = useState<RoleCategory | null>(null);
  const [roleOtherText, setRoleOtherText] = useState('');
  const [businessUse, setBusinessUse] = useState<BusinessUse | null>(null);
  const [discoverySource, setDiscoverySource] = useState<DiscoverySource | null>(null);
  const [discoveryOtherText, setDiscoveryOtherText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);

  const canContinueScreen1 =
    roleCategory !== null &&
    (roleCategory !== 'other' || roleOtherText.trim().length > 0);
  const canContinueScreen2 = businessUse !== null;
  const canFinishScreen3 =
    discoverySource !== null &&
    (discoverySource !== 'other' || discoveryOtherText.trim().length > 0);

  const resetState = () => {
    setStep(1);
    setRoleCategory(null);
    setRoleOtherText('');
    setBusinessUse(null);
    setDiscoverySource(null);
    setDiscoveryOtherText('');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      const hasCompleted = step === 3 && canFinishScreen3;
      if (!hasCompleted) return;
    }
    onOpenChange(next);
  };

  const handleContinue = () => {
    if (step === 1 && canContinueScreen1) setStep(2);
    else if (step === 2 && canContinueScreen2) setStep(3);
  };

  const handleFinish = async () => {
    if (
      !canFinishScreen3 ||
      roleCategory === null ||
      businessUse === null ||
      discoverySource === null ||
      isSubmitting
    )
      return;
    setIsSubmitting(true);
    try {
      await onComplete?.({
        roleCategory,
        roleOtherText: roleCategory === 'other' ? roleOtherText.trim() : undefined,
        businessUse,
        discoverySource,
        discoveryOtherText:
          discoverySource === 'other' ? discoveryOtherText.trim() : undefined
      });
      resetState();
    } catch {
      // The caller has said what went wrong; the answers stay so Finish can be tried again
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => setStep(step === 3 ? 2 : 1);

  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) {
      resetState();
    }
  }

  const preventClose = (e: Event) => e.preventDefault();

  const stepTitle =
    step === 1
      ? 'What best describes you?'
      : step === 2
        ? 'Do you see your experience being for a business?'
        : 'Where did you hear about Lureo?';
  const canAdvance = step === 1 ? canContinueScreen1 : step === 2 ? canContinueScreen2 : canFinishScreen3;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        scrim="soft"
        className="bg-lureo-surface"
        aria-describedby={undefined}
        hideCloseButton
        onPointerDownOutside={preventClose}
        onEscapeKeyDown={preventClose}
        onInteractOutside={preventClose}
      >
        <DialogHeader>
          <DialogTitle className="text-lureo-16">{stepTitle}</DialogTitle>
        </DialogHeader>

        <div className="border-b-lureo-border-width border-lureo-line bg-lureo-neutral-subtle px-6 py-4">
          <div className="flex items-center">
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              const state = n < step ? 'done' : n === step ? 'current' : 'upcoming';
              return (
                <Fragment key={label}>
                  <StepMarker number={n} label={label} state={state} />
                  {n < STEP_LABELS.length && (
                    <span
                      className={cn(
                        'mx-3 h-px flex-1 transition-colors duration-lureo-duration-slow',
                        n < step ? 'bg-lureo-emphasis' : 'bg-lureo-neutral-strong'
                      )}
                    />
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>

        <DialogBody>
          <div className="min-h-[280px]">
            {step === 1 && (
              <div className="space-y-2">
                {SCREEN_1_OPTIONS.map((opt) => (
                  <div key={opt.value}>
                    <Tile
                      selected={roleCategory === opt.value}
                      onClick={() => setRoleCategory(opt.value)}
                      label={opt.label}
                    />
                    {opt.value === 'other' && roleCategory === 'other' && (
                      <div className="mt-2">
                        <Input
                          look="outlined"
                          value={roleOtherText}
                          onChange={(e) => setRoleOtherText(e.target.value)}
                          placeholder="Please describe..."
                          autoFocus
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-2">
                {SCREEN_2_OPTIONS.map((opt) => (
                  <Tile
                    key={opt.value}
                    selected={businessUse === opt.value}
                    onClick={() => setBusinessUse(opt.value)}
                    label={opt.label}
                  />
                ))}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-2">
                {SCREEN_3_OPTIONS.map((opt) => (
                  <div key={opt.value}>
                    <Tile
                      selected={discoverySource === opt.value}
                      onClick={() => setDiscoverySource(opt.value)}
                      label={opt.label}
                    />
                    {opt.value === 'other' && discoverySource === 'other' && (
                      <div className="mt-2">
                        <Input
                          look="outlined"
                          value={discoveryOtherText}
                          onChange={(e) => setDiscoveryOtherText(e.target.value)}
                          placeholder="Please describe..."
                          autoFocus
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogBody>

        {/* Back on the left, the way forward on the right */}
        <DialogFooter split>
          {step > 1 ? (
            <LureoButton
              onClick={handleBack}
              variant="outline-2"
              suppressTooltip
              className="h-9 px-5 rounded-lureo-radius-button-pill"
            >
              Back
            </LureoButton>
          ) : (
            <span />
          )}
          <LureoButton
            onClick={step === 3 ? handleFinish : handleContinue}
            disabled={!canAdvance || isSubmitting}
            variant="secondary"
            suppressTooltip
            className="h-9 px-4 rounded-lureo-radius-button-pill"
          >
            {step === 3 ? (isSubmitting ? 'Saving…' : 'Finish') : 'Continue'}
          </LureoButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
